import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import CheckBoxCircle from '$tablet-app/src/app/components/CheckBoxCircle';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import { Recipe, RecipeIngredient } from '$tablet-app/src/app/recipes/types';
import { multiplyString } from '@akin-home/utils/strings';
import { FC } from 'react';
import styled from 'styled-components/native';

interface IngredientsListProps {
  ingredient: RecipeIngredient;
  selected?: boolean;
  multiplier?: number;
  onChange?: (ingredient: RecipeIngredient) => void;
}

const IngredientItem = (props: IngredientsListProps) => {
  const { ingredient, selected, multiplier, onChange } = props;

  const quantity = multiplyString(ingredient.quantity, multiplier || 1);
  const onPress = () => onChange?.(ingredient);

  return (
    <IngredientItemContainer
      key={ingredient.id}
      disabled={!onChange}
      onPress={onChange ? onPress : undefined}
    >
      {onChange ? (
        <CheckBoxCircle checked={!!selected} />
      ) : (
        <Text>&#8226; </Text>
      )}

      <Text>
        <Text bold>{quantity.text} </Text>

        {ingredient.unitOfMeasure ? ingredient.unitOfMeasure + ' ' : ''}
        {ingredient.name}
      </Text>
    </IngredientItemContainer>
  );
};

interface RecipeIngredientsProps {
  /** The recipe being viewed */
  recipe: Recipe;

  /** How many servings the recipe is for */
  servings?: number;

  /** Whether the recipe is a favourite */
  isFavourite?: boolean;

  /** This ingredients that are currently added to the shopping list */
  selected_ingredient_ids?: string[];

  /** Whether adding to the shopping list is currently enabled */
  enable_add_to_shopping_list?: boolean;

  /**
   * Callback for creating a shopping list.
   * This should only be present if the user has no main shopping list.
   */
  onPressCreateShoppingList?: () => void;
  onToggleSelectedIngredient?: (ingredient: RecipeIngredient) => void;
  onPressAddAllIngredients?: () => void;
  onPressRemoveAllIngredients?: () => void;
  onChangeServings?: (servings: number) => void;
}

const RecipeIngredientsView: FC<RecipeIngredientsProps> = (props) => {
  const {
    recipe,
    servings,
    selected_ingredient_ids,
    enable_add_to_shopping_list,
    onChangeServings,
    onToggleSelectedIngredient,
    onPressAddAllIngredients,
    onPressRemoveAllIngredients,
    onPressCreateShoppingList,
  } = props;

  const incrementServings = () =>
    onChangeServings?.(servings ? servings + 1 : 1);

  const decrementServings = () => {
    const adjusted_servings = (servings || 1) - 1;

    onChangeServings?.(adjusted_servings || 1);
  };

  return (
    <RightColumn>
      {onPressCreateShoppingList ? (
        <CreateNewListContainer>
          <Text weight="semibold">
            You can add ingredients to your main shopping list. Click the button
            below to create your main shopping list.
          </Text>

          <NewButton icon="tasks" onPress={onPressCreateShoppingList}>
            Create your shopping list
          </NewButton>
        </CreateNewListContainer>
      ) : null}

      <Text variant="h3" weight="semibold">
        Ingredients
      </Text>

      <ServingsControlContainer>
        <NewButton
          variant="secondary"
          icon="minus"
          onPress={decrementServings}
        />
        <ServingsText weight="semibold">{servings}</ServingsText>
        <NewButton
          variant="secondary"
          icon="plus"
          onPress={incrementServings}
        />
        <Spacer width="23px" />
        <Text>Servings</Text>
      </ServingsControlContainer>

      <Ingredients>
        {recipe.ingredients.map((ingredient) => (
          <IngredientItem
            key={ingredient.id}
            ingredient={ingredient}
            multiplier={servings}
            selected={selected_ingredient_ids?.includes(ingredient.id)}
            onChange={
              enable_add_to_shopping_list
                ? onToggleSelectedIngredient
                : undefined
            }
          />
        ))}
      </Ingredients>

      {enable_add_to_shopping_list && recipe.ingredients.length ? (
        selected_ingredient_ids &&
        selected_ingredient_ids.length === recipe.ingredients.length ? (
          <AddAllIngredientsButton
            icon="minus"
            variant="secondary"
            onPress={onPressRemoveAllIngredients}
          >
            Remove all ingredients from the shopping list
          </AddAllIngredientsButton>
        ) : (
          <AddAllIngredientsButton
            icon="plus"
            variant="secondary"
            onPress={onPressAddAllIngredients}
          >
            Add all ingredients to the shopping list
          </AddAllIngredientsButton>
        )
      ) : null}

      {recipe.utensils.length > 0 ? (
        <UtensilsContainer>
          <Text preset="h3">Utensils</Text>

          {recipe.utensils.map((utensil) => (
            <IngredientItemContainer key={utensil}>
              <Text>&#8226; </Text>
              <Text>{utensil}</Text>
            </IngredientItemContainer>
          ))}
        </UtensilsContainer>
      ) : null}
    </RightColumn>
  );
};

const RightColumn = styled.View`
  flex: 1;
  min-width: 200px;
  max-width: 100%;
`;

const CreateNewListContainer = styled.View`
  width: 100%;
  gap: 8px;
`;

const Ingredients = styled.View`
  padding: 20px 0;
  gap: 20px;
  width: 100%;
`;

const IngredientItemContainer = styled.TouchableOpacity`
  flex-direction: row;
  width: 100%;
  align-items: center;
  gap: 8px;
`;

const UtensilsContainer = styled.View`
  gap: 10px;
`;

const ServingsControlContainer = styled.View`
  flex-direction: row;
  align-items: center;
  margin: 16px 0;
  max-width: 100%;
`;

const ServingsText = styled(Text)`
  width: 50px;

  text-align: center;
`;

const AddAllIngredientsButton = styled(NewButton)`
  margin: 16px 0;
  width: 100%;
`;

export default RecipeIngredientsView;
